import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

// Entrada suave de secções ao fazer scroll — em CSS, sem framer-motion
// (07/10/2026). Antes usava framer-motion (42KB comprimidos no arranque) e
// escondia o topo da página inicial (opacity 0) até a animação correr: no
// telemóvel o conteúdo principal só aparecia aos 8,4s (LCP p75). Agora o que já
// está no ecrã ao abrir aparece logo, sem animação; só o que está mais abaixo
// anima quando entra no ecrã.

export type RevealDirection = 'up' | 'down' | 'left' | 'right' | 'zoom'

interface RevealProps {
  children: ReactNode
  direction?: RevealDirection
  delay?: number
  duration?: number
  distance?: number
  className?: string
  once?: boolean
}

function animacoesDesligadas(): boolean {
  if (typeof window === 'undefined') return true
  return (
    document.documentElement.classList.contains('no-animations') ||
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true ||
    typeof IntersectionObserver === 'undefined'
  )
}

export function Reveal({
  children,
  direction = 'up',
  delay = 0,
  duration = 0.7,
  distance = 28,
  className,
  once = true,
}: RevealProps) {
  const ref = useRef<HTMLDivElement>(null)
  // 'visivel' sem animação | 'escondido' à espera de entrar no ecrã | 'entrar' a animar
  const [estado, setEstado] = useState<'visivel' | 'escondido' | 'entrar'>('visivel')

  useEffect(() => {
    const el = ref.current
    if (!el || animacoesDesligadas()) return
    // Já no ecrã ao abrir: fica visível, sem animação (não atrasa o conteúdo principal).
    if (el.getBoundingClientRect().top < window.innerHeight) return
    setEstado('escondido')
    const obs = new IntersectionObserver(
      (entradas) => {
        for (const e of entradas) {
          if (e.isIntersecting) {
            setEstado('entrar')
            if (once) obs.disconnect()
          } else if (!once) {
            setEstado('escondido')
          }
        }
      },
      { threshold: 0.2 },
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [once])

  const deslocamento =
    direction === 'zoom' ? 'scale(0.92)'
      : direction === 'left' ? `translateX(${distance}px)`
      : direction === 'right' ? `translateX(-${distance}px)`
      : direction === 'down' ? `translateY(-${distance}px)`
      : `translateY(${distance}px)`

  const style: CSSProperties | undefined =
    estado === 'visivel' ? undefined
      : estado === 'escondido' ? { opacity: 0, transform: deslocamento }
      : {
          opacity: 1,
          transform: 'none',
          transition: `opacity ${duration}s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s, transform ${duration}s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s`,
        }

  return (
    <div ref={ref} className={cn(className)} style={style}>
      {children}
    </div>
  )
}
